import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { Alert, Col, Form, FormGroup, Input, Label, Row, Spinner } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from 'hooks';

import { apiDexExport, apiDexExportCsv } from '~constants/api';
import { getErrorMessage, serializeParams } from '~libs/axiosWrapper';
import { dateDisplayFormat } from '~libs/dayjs';

import { Button, ContainerHeader, ErrorResponse } from '~components';
import { DateInput, MultiSelect } from '~components/form-fields/inputs';
import { Stepper } from '~components/wizard-form';
import { setTitle } from '~main/actions/login';
import { getAll as getClientPackages } from '~main/actions/clients/clientPackages';
import { selectLookupClientPackages } from '~main/selectors/clients/clientPackages';
import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';

import { useLazyGetDexPreviewQuery } from '../actions/dex';
import { DexExportParams } from '../types';
import DexPreviewPanel from './DexPreviewPanel';

type Step = 'configure' | 'preview';

interface FormValues {
  date_from: string;
  date_to: string;
  de_identify: boolean;
  package_id: number[];
  service_type_id: number[];
}

const INITIAL_VALUES: FormValues = {
  date_from: '',
  date_to: '',
  de_identify: false,
  package_id: [],
  service_type_id: [],
};

const validationSchema = Yup.object({
  date_from: Yup.string()
    .required('From date is required')
    .test('date-range', 'From date must be before to date', function (date_from) {
      const { date_to } = this.parent as FormValues;
      return !date_from || !date_to || date_from <= date_to;
    }),
  date_to: Yup.string().required('To date is required'),
});

const DexExportPage: React.FC = () => {
  useEffect(() => {
    setTitle('DEX Export');
  }, []);

  const dispatch = useAppDispatch();
  const clientPackages = useAppSelector(selectLookupClientPackages);
  const rawServiceTypes = useAppSelector(selectAppointmentServiceTypes);
  const dexServiceTypes = useMemo(() => rawServiceTypes.filter((s) => s.dex_code), [rawServiceTypes]);

  useEffect(() => {
    dispatch(getClientPackages({ sort: ['name'], deleted: false }));
    dispatch(getServiceTypes());
  }, [dispatch]);

  const [step, setStep] = useState<Step>('configure');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  // Only one download outcome is ever shown at a time - starting either download, or leaving the
  // preview step, clears whatever banner (from either format) was previously shown.
  const [downloadStatus, setDownloadStatus] = useState<
    { format: 'xml' | 'csv'; state: 'success' } | { format: 'xml' | 'csv'; state: 'error'; message: string } | null
  >(null);
  // Bumped on every new download and on Previous, so a request whose response arrives after it's
  // been superseded (Previous clicked, or a later download started) can tell it's stale and skip
  // writing downloadStatus - otherwise a slow request resolving late could paint a banner for a
  // preview session (or a different format) the user has already moved on from.
  const downloadRequestId = useRef(0);

  const [triggerPreview, { data: preview, isFetching, error: previewError }] = useLazyGetDexPreviewQuery();

  const buildParams = (values: FormValues): DexExportParams => ({
    date_from: values.date_from,
    date_to: values.date_to,
    ...(values.de_identify ? { de_identify: true } : {}),
    ...(values.package_id.length ? { package_id: values.package_id } : {}),
    ...(values.service_type_id.length ? { service_type_id: values.service_type_id } : {}),
  });

  const formik = useFormik<FormValues>({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: async (values) => {
      try {
        await triggerPreview(buildParams(values)).unwrap();
        setStep('preview');
      } catch {
        // previewError state surfaces the message below the form
      }
    },
  });

  const handleDateChange = (id: string, value: string | null) => {
    formik.setFieldValue(id, value ?? '');
    formik.setFieldTouched(id, true, false);
  };

  const handleBack = () => {
    setStep('configure');
    setDownloadStatus(null);
    downloadRequestId.current += 1;
  };

  // Shared by the XML and CSV downloads - both are whole files built server-side and returned as a
  // blob with a Content-Disposition filename, so only the endpoint/default filename/format differ.
  // Bumping downloadRequestId here (not just in handleBack) also means starting one format
  // supersedes an in-flight request for the other, so the banner always reflects the most recently
  // started download rather than whichever of the two happens to resolve last.
  const downloadExport = async (
    format: 'xml' | 'csv',
    url: string,
    defaultFilename: string,
    setLoading: (value: boolean) => void,
  ) => {
    const requestId = ++downloadRequestId.current;
    setLoading(true);
    setDownloadStatus(null);
    try {
      const response = await axios.get(url, {
        params: buildParams(formik.values),
        paramsSerializer: { serialize: serializeParams },
        responseType: 'blob',
      });
      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : defaultFilename;
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([response.data]));
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
      if (downloadRequestId.current === requestId) setDownloadStatus({ format, state: 'success' });
    } catch (err) {
      const axiosErr = err as AxiosError;
      let message = 'Download failed. Please try again.';
      const data = axiosErr.response?.data;
      if (data instanceof Blob) {
        try {
          message = getErrorMessage(JSON.parse(await data.text()));
        } catch {
          // keep fallback message - error body wasn't valid JSON
        }
      } else if (data) {
        message = getErrorMessage(data);
      }
      if (downloadRequestId.current === requestId) setDownloadStatus({ format, state: 'error', message });
    } finally {
      setLoading(false);
    }
  };

  const isAnyDownloading = isDownloading || isDownloadingCsv;

  const handleDownload = () => downloadExport('xml', apiDexExport, 'dex-export.xml', setIsDownloading);

  const handleDownloadCsv = () => downloadExport('csv', apiDexExportCsv, 'dex-export.csv', setIsDownloadingCsv);

  const readinessUrl = `/dex/readiness?date_from=${encodeURIComponent(formik.values.date_from)}&date_to=${encodeURIComponent(formik.values.date_to)}`;

  return (
    <div className="dex-export-page p-2" data-cy="dex-export-page">
      <ContainerHeader dataCy="dex-export-header">DEX Export (Beta)</ContainerHeader>

      <div className="wizard-form">
        <Stepper
          currentStep={step === 'configure' ? 0 : 1}
          steps={[
            { title: 'Configure', step: 0, fields: {} },
            { title: 'Preview', step: 1, fields: {} },
          ]}
        />

        <div className="d-flex flex-column flex-grow-1">
          <div className="wizard-form-content">
            {step === 'configure' && (
              <>
                <div className="p-2 ps-4 pe-4">
                  <p className="text-muted mb-3">
                    Select a date range and download the DEX XML file for submission to DSS, or a CSV copy of the
                    same data for internal record-keeping.
                  </p>

                  <Form onSubmit={formik.handleSubmit}>
                    <Row>
                      <Col md={3}>
                        <FormGroup>
                          <Label for="date_from">From Date</Label>
                          <DateInput
                            type="date"
                            id="date_from"
                            name="date_from"
                            value={formik.values.date_from}
                            onChange={handleDateChange}
                            dataCy="dex-from-date"
                            maxDate={formik.values.date_to || undefined}
                            displayFormat={dateDisplayFormat}
                          />
                          {formik.touched.date_from && formik.errors.date_from && (
                            <div className="invalid-feedback d-block" data-cy="dex-from-date-error">
                              {formik.errors.date_from}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label for="date_to">To Date</Label>
                          <DateInput
                            type="date"
                            id="date_to"
                            name="date_to"
                            value={formik.values.date_to}
                            onChange={handleDateChange}
                            dataCy="dex-to-date"
                            minDate={formik.values.date_from || undefined}
                            displayFormat={dateDisplayFormat}
                          />
                          {formik.touched.date_to && formik.errors.date_to && (
                            <div className="invalid-feedback d-block" data-cy="dex-to-date-error">
                              {formik.errors.date_to}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="dex-de-identify"
                        name="de_identify"
                        className="form-check-input"
                        checked={formik.values.de_identify}
                        onChange={formik.handleChange}
                        data-cy="dex-de-identify-checkbox"
                      />
                      <Label check for="dex-de-identify">
                        De-identify export (omit client names and addresses, use SLK only)
                      </Label>
                    </FormGroup>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="package_id">Package</Label>
                          <div data-cy="dex-package-filter">
                            <MultiSelect
                              type="select"
                              id="package_id"
                              name="package_id"
                              value={formik.values.package_id}
                              options={clientPackages}
                              placeholder="All"
                              setFieldValue={formik.setFieldValue}
                            />
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="service_type_id">Service Type</Label>
                          <div data-cy="dex-service-type-filter">
                            <MultiSelect
                              type="select"
                              id="service_type_id"
                              name="service_type_id"
                              value={formik.values.service_type_id}
                              options={dexServiceTypes}
                              placeholder="All"
                              setFieldValue={formik.setFieldValue}
                            />
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>

                  {previewError && (
                    <ErrorResponse message={getErrorMessage(previewError)} className="mt-3" dataCy="dex-preview-error" />
                  )}
                </div>

                <div className="d-flex justify-content-end mt-auto pt-3">
                  <Button
                    color="success"
                    onClick={() => formik.handleSubmit()}
                    disabled={isFetching}
                    data-cy="dex-preview-button"
                  >
                    {isFetching ? <Spinner size="sm" /> : 'Preview'}
                  </Button>
                </div>
              </>
            )}

            {step === 'preview' && preview && (
              <>
                <div className="p-2 ps-4 pe-4">
                  {downloadStatus?.state === 'error' && (
                    <ErrorResponse
                      message={downloadStatus.message}
                      className="mb-3"
                      dataCy={downloadStatus.format === 'xml' ? 'dex-download-error' : 'dex-download-csv-error'}
                    />
                  )}

                  {downloadStatus?.state === 'success' && (
                    <Alert
                      color="success"
                      className="mb-3"
                      data-cy={downloadStatus.format === 'xml' ? 'dex-download-success' : 'dex-download-csv-success'}
                    >
                      {downloadStatus.format === 'xml' ? 'XML download complete.' : 'CSV download complete.'}
                    </Alert>
                  )}

                  <DexPreviewPanel preview={preview} data-cy="dex-preview-panel" />

                  {preview.issues.length > 0 && (
                    <Alert color="warning" className="mt-3" data-cy="dex-issues-cta">
                      {preview.issues.length} appointment{preview.issues.length !== 1 ? 's have' : ' has'} data quality
                      issues that may affect the export. Missing Stage 3 session data can be reviewed in the{' '}
                      <a href={readinessUrl} target="_blank" rel="noopener noreferrer">
                        DEX Readiness Report
                      </a>
                      . Client ID and package configuration issues must be resolved separately.
                    </Alert>
                  )}

                  {formik.values.de_identify && (
                    <Alert color="info" className="mt-3" data-cy="dex-csv-de-identify-note">
                      Download CSV is disabled while de-identify is selected. The CSV export always includes real
                      client names and addresses for internal record-keeping, and does not support
                      de-identification the way the XML export does.
                    </Alert>
                  )}
                </div>

                <div className="d-flex justify-content-end mt-auto pt-3">
                  <Button color="secondary" className="me-2" onClick={handleBack} data-cy="dex-back-button">
                    Previous
                  </Button>
                  <Button
                    color="success"
                    className="me-2"
                    onClick={handleDownloadCsv}
                    disabled={isAnyDownloading || formik.values.de_identify}
                    title={
                      formik.values.de_identify
                        ? 'CSV always includes real client names and addresses, and does not support de-identification.'
                        : undefined
                    }
                    data-cy="dex-download-csv-button"
                  >
                    {isDownloadingCsv ? <Spinner size="sm" /> : 'Download CSV'}
                  </Button>
                  <Button
                    color="success"
                    onClick={handleDownload}
                    disabled={isAnyDownloading}
                    data-cy="dex-download-button"
                  >
                    {isDownloading ? <Spinner size="sm" /> : 'Download XML'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DexExportPage;
